import React from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Grid } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import type { PrimaryProductKey } from '../../constants/products';
import { serveJiraNinFpsNavigation } from '../../utils/experiments/experiment-exports';
import { useAppContext } from '../app-context';

type HeaderProps = {
	input: React.ReactNode;
	filters: React.ReactNode;
	primaryProduct?: PrimaryProductKey;
};

const styles = cssMap({
	base: {
		position: 'sticky',
		top: '0',
		backgroundColor: token('elevation.surface'),
		width: '100%',
		backgroundClip: 'padding-box',
		zIndex: 1,
	},
	contentContainer: {
		margin: 'auto',
		maxWidth: '100%',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		alignItems: 'space-between',
		'@media (min-width: 48rem)': {
			maxWidth: '70rem',
		},
	},
	contentContainerWithTabs: {
		margin: 'auto',
		maxWidth: '100%',
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		alignItems: 'space-between',
		'@media (min-width: 48rem)': {
			maxWidth: '70rem',
		},
	},
	keyline: {
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	},
	grid: {
		gridTemplateAreas: '"input ." "filters filters"',
		gridTemplateColumns: '65fr 35fr',
		paddingBottom: token('space.250'),
	},
	paddingTop: {
		paddingTop: token('space.300'),
	},
	inputGridArea: {
		gridArea: 'input',
		display: 'flex',
		alignItems: 'center',
	},
	filtersGridArea: {
		gridArea: 'filters',
	},
});

export const Header = ({ input, filters, primaryProduct }: HeaderProps): React.JSX.Element => {
	const { isNav4Enabled } = useAppContext();

	const cohort = serveJiraNinFpsNavigation({ primaryProduct, fireLayerExposure: false });

	return (
		<>
			<Box xcss={cx(styles.base, styles.keyline)} testId="search-page-header">
				<Box xcss={cohort === 'tabs' ? styles.contentContainerWithTabs : styles.contentContainer}>
					<Grid
						xcss={cx(styles.grid, !isNav4Enabled && styles.paddingTop)}
						columnGap="space.200"
						rowGap="space.200"
					>
						{!isNav4Enabled && <Box xcss={styles.inputGridArea}>{input}</Box>}
						<Box xcss={styles.filtersGridArea}>{filters}</Box>
					</Grid>
				</Box>
			</Box>
			{isNav4Enabled && <VisuallyHidden>{input}</VisuallyHidden>}
		</>
	);
};
